import React, { Children } from 'react'

import HHSectionPlaceholder from 'components/common/HHSectionPlaceholder'
import { getExceptionDate } from 'components/customer-details/content/work-orders/actions/exception/utils'
import { convertDateTimeToPreferedTimezone } from 'data/dateTime/convertDateTimeToPreferedTimezone'
import T from 'T'
import { getExceptionMediaSlides } from 'utils/exceptionImage'
import { get } from 'utils/lodash'

import ExceptionCard from './exception-card/ExceptionCard'
import ExceptionGallery from './ExceptionGallery'
import ExceptionSwiperContainer from './ExceptionSwiperContainer'
import useGetExceptionProps from './useGetExceptionProps'

import type { WorkOrderContainer } from '../containers/types'
import type { ExceptionCardClickHandler, ExceptionShape } from './exception-card/types'

const HHSectionPlaceholderUntyped = HHSectionPlaceholder as React.ElementType
const ExceptionGalleryUntyped = ExceptionGallery as React.ElementType

type ExceptionsContentProps = {
  accountId?: string
  workOrderId: string
  allExceptions?: unknown[]
  currentException?: unknown
  isExceptionGalleryViewOpen: boolean
  handleCardClick: ExceptionCardClickHandler
  handleGalleryClose: () => void
  containers?: WorkOrderContainer[]
  serviceAction?: string | null
  exceptionVersions?: Record<string, unknown[]>
}

const ExceptionsContent: React.FC<ExceptionsContentProps> = ({
  accountId,
  workOrderId,
  allExceptions,
  currentException,
  isExceptionGalleryViewOpen,
  handleCardClick,
  handleGalleryClose,
  containers,
  serviceAction,
  exceptionVersions,
}) => {
  const { PageProps, ImageSx } = useGetExceptionProps()

  const hasExceptions = Array.isArray(allExceptions) && allExceptions.length > 0

  return (
    <>
      {!hasExceptions && <HHSectionPlaceholderUntyped title={T.NO_WO_EXCEPTIONS} />}
      {hasExceptions && (
        <ExceptionSwiperContainer exceptions={allExceptions}>
          {Children.toArray(
            allExceptions.map(exception => {
              const exceptionType = get(exception, 'exceptionType')
              const exceptionDate = getExceptionDate(exception)
              const { onlyDate, onlyTime } = convertDateTimeToPreferedTimezone({ dateTime: exceptionDate })
              const note = get(exception, 'noteText', '')
              const description = get(exception, 'description', '')
              const images = getExceptionMediaSlides(exception)
              const id = get(exception, 'id')
              const revisions = get(exceptionVersions, `${id}`, undefined)

              return (
                <swiper-slide key={`exception-card-${id}`}>
                  <ExceptionCard
                    accountId={accountId}
                    containers={containers}
                    date={onlyDate}
                    description={description}
                    exception={exception as ExceptionShape}
                    exceptionType={exceptionType}
                    images={images}
                    ImageSx={ImageSx}
                    note={note}
                    PageProps={PageProps}
                    revisions={revisions}
                    serviceAction={serviceAction}
                    time={onlyTime}
                    workOrderId={workOrderId}
                    onClick={handleCardClick}
                  />
                </swiper-slide>
              )
            })
          )}
        </ExceptionSwiperContainer>
      )}
      <ExceptionGalleryUntyped
        accountId={accountId}
        exception={currentException}
        open={isExceptionGalleryViewOpen}
        workOrderId={workOrderId}
        onClose={handleGalleryClose}
      />
    </>
  )
}

export default ExceptionsContent
